// /src/features/auth/hooks/usePermissions.js
import { useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { tokenService } from '../services/tokenService';

export function usePermissions() {
  const { isAuthenticated, user } = useAuth();

  const permissions = useMemo(() => {
    if (!isAuthenticated) {
      return {
        scopes: [],
        hasScope: () => false,
        hasAnyScope: () => false,
        hasAllScopes: () => false,
        isAdmin: false,
        isSuperAdmin: false,
      };
    }

    const scopes = tokenService.getScopes();
    const isSuperAdmin = scopes.includes('*');
    const isAdmin = isSuperAdmin || scopes.some(scope => scope.includes('admin:'));

    return {
      scopes,
      hasScope: (scope) => tokenService.hasScope(scope),
      hasAnyScope: (scopeList) => tokenService.hasAnyScope(scopeList),
      hasAllScopes: (scopeList) => scopeList.every(scope => tokenService.hasScope(scope)),
      isAdmin,
      isSuperAdmin,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);  // User object used in token service

  return permissions;
}

// Specific permission hooks for common use cases
export function useAdminPermissions() {
  const permissions = usePermissions();
  return {
    isAdmin: permissions.isAdmin,
    isSuperAdmin: permissions.isSuperAdmin,
    canManageUsers: permissions.hasScope('staff:create') || permissions.hasScope('staff:*'),
    canManageRoles: permissions.hasScope('role:create') || permissions.hasScope('role:*'),
    canManageDepartments: permissions.hasScope('department:create') || permissions.hasScope('department:*'),
    canViewErrors: permissions.hasScope('admin:read') || permissions.hasScope('admin:*'),
  };
}

export function useStaffPermissions() {
  const permissions = usePermissions();
  return {
    canReadStaff: permissions.hasScope('staff:read'),
    canCreateStaff: permissions.hasScope('staff:create'),
    canUpdateStaff: permissions.hasScope('staff:update'),
    canDeleteStaff: permissions.hasScope('staff:delete'),
    canAssignRoles: permissions.hasScope('staff:role:assign'),
    canRemoveRoles: permissions.hasScope('staff:role:remove'),
  };
}

export function useTaskPermissions() {
  const permissions = usePermissions();
  const equipmentPermissions = useEquipmentPermissions();
  const filePermissions = useFilePermissions();
  return {
    canRead: permissions.hasScope('task:read'),
    canCreate: permissions.hasScope('task:create'),
    canUpdate: permissions.hasScope('task:update'),
    canDelete: permissions.hasScope('task:delete'),
    canCancel: permissions.hasScope('task:state:cancel'),
    canComplete: permissions.hasScope('task:state:complete'),
    canHold: permissions.hasScope('task:state:hold'),
    canReopen: permissions.hasScope('task:state:reopen'),
    canResume: permissions.hasScope('task:state:resume'),
    canStart: permissions.hasScope('task:state:start'),
    canViewTransitions: permissions.hasScope('task:state:transitions'),
    canUpdateState: permissions.hasScope('task:state:update'),
    canApproveCompletion: permissions.hasScope('task:completion:approve'),
    // Combined permission for adding/removing equipment from tasks
    canModifyEquipment: equipmentPermissions.canRead && permissions.hasScope('task:update'),
    // Combined permission for adding/removing manuals from tasks
    canCreateManuals: filePermissions.canRead && filePermissions.canUpload && permissions.hasScope('task:create'),
    canModifyManuals: filePermissions.canRead && filePermissions.canUpload && permissions.hasScope('task:update'),
  };
}

export function useTaskCategoryPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('task-category:read'),
    canCreate: permissions.hasScope('task-category:create'),
    canUpdate: permissions.hasScope('task-category:update'),
    canDelete: permissions.hasScope('task-category:delete'),
    canActivate: permissions.hasScope('task-category:activate'),
    canDeactivate: permissions.hasScope('task-category:deactivate'),
  };
}

export function useRolePermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('role:read'),
    canCreate: permissions.hasScope('role:create'),
    canUpdate: permissions.hasScope('role:update'),
    canDelete: permissions.hasScope('role:delete'),
  };
}

export function useEquipmentPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('equipment:read'),
    canCreate: permissions.hasScope('equipment:create'),
    canUpdate: permissions.hasScope('equipment:update'),
    canDelete: permissions.hasScope('equipment:delete'),
    canActivate: permissions.hasScope('equipment:reactivate'),
    canDeactivate: permissions.hasScope('equipment:deactivate'),
  };
}

export function useRoomPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('room:read'),
    canCreate: permissions.hasScope('room:create'),
    canUpdate: permissions.hasScope('room:update'),
    canDelete: permissions.hasScope('room:delete'),
    canActivate: permissions.hasScope('room:reactivate'),
    canDeactivate: permissions.hasScope('room:deactivate'),
  };
}

export function useFilePermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('file:read'),
    canUpload: permissions.hasScope('file:upload'),
    canDownload: permissions.hasScope('file:download'),
    canDelete: permissions.hasScope('file:delete'),
    canManage: permissions.hasScope('file:manage'),
    // Set who may see and change files and folders (and see all of them)
    canManageAccess: permissions.hasScope('file:permissions'),
  };
}

export function useInfoscreenPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('infoscreen:read'),
    canCreate: permissions.hasScope('infoscreen:create'),
    canUpdate: permissions.hasScope('infoscreen:update'),
    canDelete: permissions.hasScope('infoscreen:delete'),
  };
}

export function useProcedurePermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('procedure:read'),
    canCreate: permissions.hasScope('procedure:create'),
    canUpdate: permissions.hasScope('procedure:update'),
    canDelete: permissions.hasScope('procedure:delete'),
    canLink: permissions.hasScope('procedure:link'),
  };
}

export function useFeedbackPermissions() {
  const permissions = usePermissions();
  return {
    canModerate: permissions.hasScope('feedback:moderate'),
  };
}

export function useSoftwareLicensePermissions() {
  const permissions = usePermissions();
  return {
    canModerate: permissions.hasScope('software_license:moderate'),
  };
}

export function useAdminDepartmentPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('admin-department:read'),
    canCreate: permissions.hasScope('admin-department:create'),
    canUpdate: permissions.hasScope('admin-department:update'),
    canDelete: permissions.hasScope('admin-department:delete'),
  };
}

export function useAdminModulePermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('admin-module:read'),
    canEnable: permissions.hasScope('admin-module:enable'),
    canDisable: permissions.hasScope('admin-module:disable'),
  };
}

export function useAdminErrorPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('admin-error:read'),
    canAnalyze: permissions.hasScope('admin-error:analyze'),
    canCleanup: permissions.hasScope('admin-error:cleanup'),
    canDelete: permissions.hasScope('admin-error:delete'),
  };
}

export function useAdminPermissionPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('admin-permission:read'),
  };
}

export function useProductGroupPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('inventory:product-group:read'),
    canCreate: permissions.hasScope('inventory:product-group:create'),
    canUpdate: permissions.hasScope('inventory:product-group:update'),
    canDelete: permissions.hasScope('inventory:product-group:delete'),
  };
}

export function useInventoryItemPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('inventory:item:read'),
    canCreate: permissions.hasScope('inventory:item:create'),
    canUpdate: permissions.hasScope('inventory:item:update'),
    canDelete: permissions.hasScope('inventory:item:delete'),
  };
}

export function useInventoryMSDSPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('inventory:msds:read'),
    canCreate: permissions.hasScope('inventory:msds:create'),
    canUpdate: permissions.hasScope('inventory:msds:update'),
    canDelete: permissions.hasScope('inventory:msds:delete'),
  };
}

export function useInventoryLotPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('inventory:lot:read'),
    canCreate: permissions.hasScope('inventory:lot:create'),
    canUpdate: permissions.hasScope('inventory:lot:update'),
  };
}

export function useInventorySupplierPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('inventory:supplier:read'),
    canCreate: permissions.hasScope('inventory:supplier:create'),
    canUpdate: permissions.hasScope('inventory:supplier:update'),
    canDelete: permissions.hasScope('inventory:supplier:delete'),
  };
}

export function useInventoryStockPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('inventory:stock:read'),
    canRecord: permissions.hasScope('inventory:stock:record'),
  };
}

// Cost prices and stock value: staff who only book stock never see prices
export function useInventoryCostPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('inventory:cost:read'),
    canUpdate: permissions.hasScope('inventory:cost:update'),
  };
}

export function useDelegationPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('delegation:read'),
    canCreate: permissions.hasScope('delegation:create'),
    canRevise: permissions.hasScope('delegation:revise'),
    canSign: permissions.hasScope('delegation:sign'),
    canArchive: permissions.hasScope('delegation:archive'),
    canDelete: permissions.hasScope('delegation:delete'),
  };
}

export function useReportingPermissions() {
  const permissions = usePermissions();
  return {
    canGenerate: permissions.hasScope('report:generate'),
  };
}

export function useTemplateAdminPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('administration-template:read'),
    canCreate: permissions.hasScope('administration-template:create'),
    canUpdate: permissions.hasScope('administration-template:update'),
    canDelete: permissions.hasScope('administration-template:delete'),
  };
}

export function useStockLocationPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('inventory:stock:read'),
    canCreate: permissions.hasScope('inventory:stock:manage'),
    canUpdate: permissions.hasScope('inventory:stock:manage'),
    canDelete: permissions.hasScope('inventory:stock:manage'),
  };
}

export function useScannerPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('inventory:scanner:read'),
    canCreate: permissions.hasScope('inventory:scanner:create'),
    canUpdate: permissions.hasScope('inventory:scanner:update'),
    canDelete: permissions.hasScope('inventory:scanner:delete'),
  };
}

export function useTidsregistreringRegistrationPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('tidsregistering:registration:read'),
    canCreate: permissions.hasScope('tidsregistering:registration:create'),
    canUpdate: permissions.hasScope('tidsregistering:registration:update'),
    canDelete: permissions.hasScope('tidsregistering:registration:delete'),
  };
}

export function useTidsregistreringAbsencePermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('tidsregistering:absence:read'),
    canCreate: permissions.hasScope('tidsregistering:absence:create'),
    canUpdate: permissions.hasScope('tidsregistering:absence:update'),
    canDelete: permissions.hasScope('tidsregistering:absence:delete'),
    canApprove: permissions.hasScope('tidsregistering:absence:approve'),
  };
}

export function useTidsregistreringDayOverviewPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('tidsregistering:day_overview:read'),
    canApprove: permissions.hasScope('tidsregistering:day_overview:approve'),
    canAdminApprove: permissions.hasScope('tidsregistering:day_overview:admin_approve'),
  };
}

export function useTidsregistreringPlannedShiftPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('tidsregistering:planned_shift:read'),
    canCreate: permissions.hasScope('tidsregistering:planned_shift:create'),
    canUpdate: permissions.hasScope('tidsregistering:planned_shift:update'),
    canDelete: permissions.hasScope('tidsregistering:planned_shift:delete'),
  };
}

export function useTidsregistreringRegistrationCodePermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('tidsregistering:registration_code:read'),
    canCreate: permissions.hasScope('tidsregistering:registration_code:create'),
    canUpdate: permissions.hasScope('tidsregistering:registration_code:update'),
    canDelete: permissions.hasScope('tidsregistering:registration_code:delete'),
  };
}

export function useTidsregistreringClosingPeriodPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('tidsregistering:closing_period:read'),
    canCreate: permissions.hasScope('tidsregistering:closing_period:create'),
    canExport: permissions.hasScope('tidsregistering:closing_period:export'),
  };
}

export function useNonWorkingDayPermissions() {
  const permissions = usePermissions();
  return {
    // Not scope-gated on the backend (any authenticated user can read
    // non-working days — they're foundational data for task scheduling,
    // absence balances, and the gantt chart), so always true here rather
    // than checking a scope the API no longer enforces.
    canRead: true,
    canCreate: permissions.hasScope('tidsregistering:non_working_day:create'),
    canUpdate: permissions.hasScope('tidsregistering:non_working_day:update'),
    canDelete: permissions.hasScope('tidsregistering:non_working_day:delete'),
    canAdminGenerate: permissions.hasScope('tidsregistering:non_working_day:admin_generate'),
  };
}

export function useTidsregistreringOverenskomstPermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('tidsregistering:overenskomst:read'),
    canCreate: permissions.hasScope('tidsregistering:overenskomst:create'),
    canUpdate: permissions.hasScope('tidsregistering:overenskomst:update'),
    canDelete: permissions.hasScope('tidsregistering:overenskomst:delete'),
  };
}

export function useTidsregistreringSickLeavePermissions() {
  const permissions = usePermissions();
  return {
    canRead: permissions.hasScope('tidsregistering:sick_leave:read'),
    canAdmin: permissions.hasScope('tidsregistering:sick_leave:admin'),
  };
}